import {
	Box,
	Button,
	Divider,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { ArrowLeftIcon, FileTextIcon, PlusIcon } from "lucide-react";
import { type FC, useState } from "react";
import { useParams } from "react-router-dom";
import { useDocumentListQuery } from "web_myplann/Api/Document/useDocumentListQuery";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AddDocumentDrawerRedesign } from "web_myplann/Portfolio/Components/Redesign/Documents/AddDocumentDrawerRedesign";
import { DocumentItemRedesign } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentItemRedesign";
import { DocumentListSkeleton } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentListSkeleton";
import { IconBox } from "web_myplann/Shared/Components/IconBox";
import { ListCountBarRedesign } from "web_myplann/Shared/Components/Redesign/ListCountBarRedesign";
import { MyPlannTopBarRedesign } from "web_myplann/Shared/Components/Redesign/MyPlannTopBarRedesign";
import { RedesignListBackground } from "web_myplann/Shared/Components/Redesign/RedesignListBackground";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import { useGoBack } from "web_myplann/Shared/Hooks/useGoBack";

export const FolderDetailPageRedesign: FC = () => {
	const { tag } = useParams();
	const theme = useTheme();
	const { t } = useResource();
	const navigateBack = useGoBack(Routing.Portfolio.DocumentsList.path());
	const { data, isLoading } = useDocumentListQuery(tag);

	const [documentDrawer, setDocumentDrawer] = useState(false);

	const documents = data?.documents ?? [];
	const isEmpty = documents.length === 0;

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<Stack
					sx={{
						width: "100%",
						bgcolor: theme.palette.background.paper,
					}}
				>
					<MyPlannTopBarRedesign
						title={tag ?? ""}
						startComponent={
							<IconButton onClick={navigateBack}>
								<ArrowLeftIcon
									size={theme.iconSize.md}
									color={theme.palette.grey[600]}
								/>
							</IconButton>
						}
					/>
				</Stack>
			}
		>
			{isLoading ? (
				<DocumentListSkeleton />
			) : isEmpty ? (
				<Stack
					alignItems="center"
					justifyContent="center"
					height="85vh"
					gap={2}
					px={2.5}
				>
					<IconBox bg={theme.palette.grey[100]} size={64}>
						<FileTextIcon size={28} color={theme.palette.grey[400]} />
					</IconBox>
					<Typography variant="body2" textAlign="center">
						{t(Resources.DocumentDetail.noDocuments)}
					</Typography>
					<Button
						variant="primary"
						startIcon={<PlusIcon size={theme.iconSize.sm} />}
						onClick={() => setDocumentDrawer(true)}
					>
						{t(Resources.Portfolio.List.Redesign.Documents.addDocument)}
					</Button>
				</Stack>
			) : (
				<RedesignListBackground pt={theme.spacingTokens.sm}>
					<ListCountBarRedesign
						count={documents.length}
						labelKey={
							Resources.Portfolio.List.Redesign.Documents.documentsCount
						}
					/>

					<Box bgcolor="background.paper">
						<Stack
							divider={
								<Divider sx={{ borderColor: theme.palette.grey[100] }} />
							}
						>
							{documents.map((document) => (
								<DocumentItemRedesign
									key={document.id}
									item={document}
									activeTag={tag}
								/>
							))}
						</Stack>
					</Box>

					<Box px={theme.spacingTokens.lg} mt={theme.spacingTokens.xl}>
						<Button
							variant="secondary"
							startIcon={<PlusIcon size={theme.iconSize.md} />}
							onClick={() => setDocumentDrawer(true)}
							sx={{ color: theme.palette.blue[600] }}
						>
							{t(Resources.Portfolio.List.Redesign.Documents.addDocument)}
						</Button>
					</Box>
				</RedesignListBackground>
			)}

			<AddDocumentDrawerRedesign
				open={documentDrawer}
				onClose={() => setDocumentDrawer(false)}
			/>
		</MyPlannLayout>
	);
};
